import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { CapacitorHttp } from '@capacitor/core';

import { OktaAuthService } from './okta-auth.service';
import { UserService } from './user-service.service';
import { UpdateCheckService } from './mobile-app-update-check-service';
import { HybridStorageKeys, HybridStorageService } from '../app/services/hybrid-storage.service';
import { DemoModeService } from './demo-mode.service';
import { environment } from '../environments/environment';

@Injectable({ providedIn: 'root' })
export class DeepLinkService {
  private isCheckingAuth = false;
  private readonly fiveMinutes = 1 * 60 * 1000;
  private lastHandledDeepLink: string | null = null;
  private _updateCheckService?: UpdateCheckService;

  constructor(
    private router: Router,
    private zone: NgZone,
    private oktaAuthService: OktaAuthService,
    private userService: UserService,
    private hybridStorageService: HybridStorageService,
    private updateCheckService: UpdateCheckService,
    private demoModeService: DemoModeService
  ) {
    // Custom cold-start event from native
    window.addEventListener('forcedAppUrlOpen' as any, async (e: any) => {
      const url = e?.detail?.url as string;
      if (!url || url === this.lastHandledDeepLink) return;
      this.lastHandledDeepLink = url;
      await this.handleDeepLink(url);
    });

    // Standard Capacitor event (warm start; possibly cold if plugin fires late)
    App.addListener('appUrlOpen', async (event) => {
      const url = event?.url;
      if (!url || url === this.lastHandledDeepLink) return;
      this.lastHandledDeepLink = url;
      await this.handleDeepLink(url);
    });

    // Extra safety: query the launch URL once on boot
    this.checkLaunchUrl();
  }

  // ---------- helpers ----------

  private isDemoDeepLink(url: string): boolean {
    if (!url) return false;
    const u = url.toLowerCase();

    const hasQueryDemo =
      /\?(?:[^#]*&)?demo(?:=|&|$)/.test(u) ||
      /[?&]mode=demo(?:=|&|$)/.test(u);

    return (
      hasQueryDemo ||
      u.includes('com.nhaschools.app://demo') ||
      u.endsWith('/demo')
    );
  }

  private isAuthCallback(url: string): boolean {
    const u = url.toLowerCase();
    return u.includes('/callback') || u.includes(':/callback') || u.includes('oauth') || u.includes('token');
  }

  private isLogoutCallback(url: string): boolean {
    const u = url.toLowerCase();
    return u.includes('/logout') || u.includes(':/logout');
  }

  // ---------- cold start probe ----------

  private async checkLaunchUrl() {
    if (!Capacitor.isNativePlatform()) return;

    // Capture the loaded path synchronously up front — the wildcard route redirect can rewrite it
    // during the awaits below.
    const bootPath = window.location.pathname + window.location.search;

    // Don't process launch URL if we're in an OAuth callback
    const currentUrl = window.location.href;
    if (currentUrl.includes('/callback') && currentUrl.includes('code=') && currentUrl.includes('state=')) {

      return;
    }

    // The native layer delivers deep links by RE-LOADING the webview at the target path — on cold
    // start AND warm resume, both platforms. For a short link that loaded path is the most reliable
    // source: App.getLaunchUrl() goes stale on resume (returns the original process-launch URL), and
    // the appUrlOpen event can be dropped when the reload tears down the previous webview instance
    // (Android). iOS loads `/s/<code>`; Android prefixes it to `/apps/s/<code>` — match either by
    // anchoring on the trailing `/s/<code>`. Resolving straight from here makes resume deterministic
    // without depending on event timing.
    const shortMatch = bootPath.match(/\/s\/([A-Za-z0-9]{6})(?:$|\?)/);
    if (shortMatch) {
      const shortRoute = `/s/${shortMatch[1]}`;
      if (shortRoute !== this.lastHandledDeepLink) {
        this.lastHandledDeepLink = shortRoute;
        await this.handleDeepLink(shortRoute);
      }
      return;
    }

    try {
      let tries = 0;
      let launch = await App.getLaunchUrl();
      while (!launch?.url && tries < 3) {
        await new Promise(r => setTimeout(r, 50));
        tries++;
        launch = await App.getLaunchUrl();
      }

      const url = launch?.url ?? '';
      if (!url || url === this.lastHandledDeepLink) return;
      this.lastHandledDeepLink = url;

      await this.handleDeepLink(url);
    }
    catch (error) {
      console.error('[DeepLink] Error checking launch URL:', error);
    }
    finally {}
  }

  // ---------- core handler ----------

  async handleDeepLink(_url: string) {
    const url = _url || '';
    if (!url) return;

    // Ignore logout callbacks - they should not trigger deep link handling
    if (this.isLogoutCallback(url)) {
      // Android only: the Custom Tab logout (OktaAuthService.signOutViaCustomTab) redirected back via
      // com.nhaschools.app:/logout once Okta ended the SSO session. Dismiss the tab; the native layer
      // (MainActivity) reloads the WebView at home. No-op on iOS/web (closeAuthBrowser is guarded by
      // an internal flag).
      await this.oktaAuthService.closeAuthBrowser();
      return;
    }

    // Ignore auth callbacks - they are handled directly by LoginCallbackComponent.
    // Storing these as pending deep links causes LoginCallbackComponent to re-navigate
    // to /callback with an already-consumed OAuth code after the token exchange completes.
    if (this.isAuthCallback(url)) {
      // Android only: a Chrome Custom Tab login just redirected back via the custom scheme. Dismiss
      // the tab; the native layer (MainActivity) reloads the WebView at /callback so LoginCallbackComponent
      // can finish the token exchange. No-op on iOS/web (closeAuthBrowser is guarded by an internal flag).
      await this.oktaAuthService.closeAuthBrowser();
      return;
    }

    // Check if this is a demo deep link (from external source, not internal navigation)
    const isExternalDemoLink = this.isDemoDeepLink(url) && (/^https?:\/\//.test(url) || /^[a-zA-Z]+:\/\//.test(url));

    if (isExternalDemoLink) {

      await this.handleDemoModeDeepLink();
      return;
    }

    // If demo mode is active and this is NOT a demo link or auth callback, exit demo mode
    if (this.demoModeService.isDemoMode && !this.isAuthCallback(url) && !this.isDemoDeepLink(url)) {

      await this.handleExitDemoModeDeepLink();
      return;
    }

    // Normal routing for non-demo links
    let route: string;
    if (/^https?:\/\//.test(url) || /^[a-zA-Z]+:\/\//.test(url)) {
      const urlObj = new URL(url);
      route = urlObj.pathname + urlObj.search;
    } else {
      route = url;
    }

    // Check if this is a short URL that needs to be resolved
    // Pattern: /s/{shortCode} (e.g., /s/aB3xY9)
    const shortUrlMatch = route.match(/^\/s\/([A-Za-z0-9]{6})$/);
    if (shortUrlMatch) {
      const shortCode = shortUrlMatch[1];
      // Flag a deep link as in-flight BEFORE the (network-bound) resolution so the cold-start
      // consumers (AppComponent.navigateAfterColdStartAuth + HomeComponent) don't race us to
      // /mainmenu while we're still resolving. This service is the single owner of short-link
      // navigation; the others defer to it while this flag is set.
      this.markDeepLinkPending();
      try {
        const resolvedRoute = await this.resolveShortCodeToRoute(shortCode);
        // Always clear the dedup guard once we're done, success or fail — otherwise a failed
        // resolution leaves lastHandledDeepLink stuck on '/s/<code>' and the NEXT identical tap
        // is silently swallowed (the "had to tap 3-4 times" symptom).
        this.lastHandledDeepLink = null;
        if (resolvedRoute) {
          await this.navigateOrStore(resolvedRoute);
        } else {
          console.warn('[DeepLink] Could not resolve short URL:', shortCode);
          await this.fallbackToHome();
        }
      } finally {
        this.clearDeepLinkPending();
      }
      return;
    }

    // Regular (already-resolved) deep links — behavior unchanged.
    const isAuthenticated = await this.userService.isUserAuthenticated();
    if (isAuthenticated) {
      this.zone.run(() => this.router.navigateByUrl(route));
    } else {
      await this.hybridStorageService.setItem(HybridStorageKeys.Capacitor_DeepLinkPath, route);
    }
  }

  // ---------- short-link resolution + navigation (short links only) ----------

  /**
   * Resolve a 6-char short code to its in-app route (pathname + query) via the backend /s/{code}
   * endpoint, which 302-redirects to the full URL. Returns just the route portion, or null if
   * resolution fails. Native only — on web the server follows the /s redirect before Angular ever
   * loads, so this is never invoked there.
   *
   * Cross-platform redirect handling:
   *  - Android honors `disableRedirects`, so the 302's `Location` header is present → use it.
   *  - iOS (URLSession) ignores `disableRedirects` and FOLLOWS the 302 automatically, so there's no
   *    `Location` header — but the response reports the final URL it landed on via `response.url`.
   *    Falling back to that is what makes short links resolve on iOS instead of dropping to /mainmenu.
   */
  private async resolveShortCodeToRoute(shortCode: string): Promise<string | null> {
    const requestUrl = `${environment.baseUrl}s/${shortCode}`;
    const toRoute = (u: string): string => {
      if (/^https?:\/\//.test(u) || /^[a-zA-Z]+:\/\//.test(u)) {
        const parsed = new URL(u);
        return parsed.pathname + parsed.search;
      }
      return u;
    };
    try {
      // Use baseUrl (not apiUrl): ShortUrlController is mounted at /s, not /api/s.
      const response = await CapacitorHttp.request({
        url: requestUrl,
        method: 'GET',
        disableRedirects: true // honored on Android (gives us the Location header); ignored on iOS
      });

      // [DLDIAG] One-line snapshot so a single device/sim run shows exactly what iOS returns.
      console.warn('[DLDIAG] resolve', shortCode,
        '| status:', (response as any)?.status,
        '| response.url:', (response as any)?.url,
        '| Location:', response?.headers?.['Location'] || response?.headers?.['location']);

      // Primary (Android): the redirect Location header.
      const longUrl = response.headers?.['Location'] || response.headers?.['location'];
      if (longUrl) {
        return toRoute(longUrl);
      }

      // Fallback (iOS): the final URL after the redirect was auto-followed. Guard against it just
      // being the /s/<code> request URL (i.e. no redirect actually happened / 404).
      const finalUrl: string | undefined = (response as any)?.url;
      if (finalUrl && finalUrl !== requestUrl && !/\/s\/[A-Za-z0-9]{6}(?:$|\?)/.test(finalUrl)) {
        return toRoute(finalUrl);
      }

      console.warn('[DeepLink] Short URL did not resolve (no Location, no usable final url):', shortCode, (response as any)?.status);
      return null;
    } catch (error) {
      console.error('[DeepLink] Failed to resolve short URL:', shortCode, error);
      return null;
    }
  }

  /**
   * Navigate to a resolved SHORT-LINK route now if we can — logged in, OR holding a still-valid
   * refresh token (OktaAuthGuard refreshes in the background, see okta-auth-guard.ts). Otherwise
   * stash it so LoginCallbackComponent navigates there after the user authenticates.
   *
   * NOTE: this is intentionally more permissive than the regular-deep-link path above.
   * isUserAuthenticated() only checks the ACCESS token, which is routinely expired mid-cold-start;
   * for short links we are the SOLE navigator (the other consumers defer via deepLinkPending), so
   * we must not fall through to "store and hope someone else navigates".
   */
  private async navigateOrStore(route: string): Promise<void> {
    if (await this.canNavigateNow()) {
      // Await the navigation so callers (the short-link branch) only clear deepLinkPending AFTER
      // the router has actually landed on the resolved route. That closes the residual race where
      // a slower cold-start consumer finishes after us, sees the flag already cleared, and slips a
      // /mainmenu navigation through — by the time the flag clears, the router is on the real page
      // (no longer an auth route), so those consumers' own guards keep them from redirecting.
      await this.zone.run(() => this.router.navigateByUrl(route));
    } else {
      await this.hybridStorageService.setItem(HybridStorageKeys.Capacitor_DeepLinkPath, route);
    }
  }

  /** Authenticated, or able to become so without a fresh login (valid refresh token). */
  private async canNavigateNow(): Promise<boolean> {
    if (await this.userService.isUserAuthenticated()) return true;
    try {
      const okta = this.oktaAuthService.getOktaAuth();
      const refreshToken = await okta.tokenManager.get('refreshToken');
      return !!refreshToken && !okta.tokenManager.hasExpired(refreshToken);
    } catch {
      return false;
    }
  }

  /** A short link we couldn't resolve shouldn't strand the user on a blank page. */
  private async fallbackToHome(): Promise<void> {
    if (await this.canNavigateNow()) {
      this.zone.run(() => this.router.navigateByUrl('/mainmenu'));
    }
  }

  // ---------- deep-link-in-flight coordination (short links only) ----------
  // Set the moment a SHORT link is detected at launch/resume; cleared once it has been resolved
  // and navigated (or stored). While set, the cold-start auth flow (AppComponent) and
  // HomeComponent must NOT fall back to /mainmenu — this service owns the navigation. This is what
  // removes the three-way race between those consumers over the single Capacitor_DeepLinkPath key.
  // Regular deep links never set this flag, so their handling is completely unaffected.

  private _deepLinkPending = false;
  private deepLinkPendingTimer: any = null;

  get deepLinkPending(): boolean {
    return this._deepLinkPending;
  }

  markDeepLinkPending(): void {
    this._deepLinkPending = true;
    // Safety valve: never let the flag wedge the app on Home if resolution never completes
    // (e.g. an unexpected throw before the finally runs).
    if (this.deepLinkPendingTimer) clearTimeout(this.deepLinkPendingTimer);
    this.deepLinkPendingTimer = setTimeout(() => { this._deepLinkPending = false; }, 15000);
  }

  clearDeepLinkPending(): void {
    this._deepLinkPending = false;
    if (this.deepLinkPendingTimer) {
      clearTimeout(this.deepLinkPendingTimer);
      this.deepLinkPendingTimer = null;
    }
  }

  // ---------- demo toggles ----------

  private async handleDemoModeDeepLink() {

    await this.demoModeService.enableDemoMode();

    try {
      const oktaAuth = this.oktaAuthService.getOktaAuth();
      if (oktaAuth && oktaAuth.tokenManager) {
        oktaAuth.tokenManager.clear();

      } else {
    
      }
    } catch (e) {
      console.error('[DeepLink] Error clearing tokens:', e);
    }

    

    // Wait for config$ subscription in OktaAuthService to fire and reinitialize Okta
    await new Promise(resolve => setTimeout(resolve, 500));

    

    // Navigate to home without reloading - let the auth flow happen naturally
    this.zone.run(() => {
      this.router.navigateByUrl('').catch(err => {
        console.error('Error navigating to home after demo mode deep link:', err);
      });
    });

    
  }

  private async handleExitDemoModeDeepLink() {
    
    await this.demoModeService.disableDemoMode();
    try { this.oktaAuthService.getOktaAuth().tokenManager.clear(); } catch {}

    if (Capacitor.isNativePlatform()) {
      window.location.reload();
    } else {
      window.location.href = '/';
    }
  }

  // ---------- (unchanged) app state & auth ----------

  async initializeDeepLinkListener() {
    if (!Capacitor.isNativePlatform()) return;

    App.addListener('appStateChange', async (state: { isActive: boolean }) => {
      if (!state.isActive) {
        await this.hybridStorageService.setItem(HybridStorageKeys.Capacitor_backgroundTimestamp, Date.now().toString());
        return;
      }

      const lastBg = parseInt(
        (await this.hybridStorageService.getItem(HybridStorageKeys.Capacitor_backgroundTimestamp)) || '0',
        10
      );

      if (lastBg > 0 && Date.now() - lastBg > this.fiveMinutes) {
        await this.oktaAuthService.refreshTokenIfNeeded();
      }

      if (this.isCheckingAuth) return;
      this.isCheckingAuth = true;
      try { await this.checkAuthentication(); }
      finally { this.isCheckingAuth = false; }
    });
  }

  async navigateIfPendingLink() {
    const path = await this.hybridStorageService.getItem<string>(HybridStorageKeys.Capacitor_DeepLinkPath);
    if (path) {
      await this.hybridStorageService.removeItem(HybridStorageKeys.Capacitor_DeepLinkPath);
      this.zone.run(() => this.router.navigateByUrl(path));
    }
  }

  private async checkAuthentication() {
    const isAuthenticated = await this.userService.isUserAuthenticated();
    if (isAuthenticated) {
      await this.userService.setUserProfile();
      return;
    }

    // isUserAuthenticated() only checks the ACCESS token, which is routinely expired on resume. Before
    // wiping the session and forcing re-login, honor a still-valid REFRESH token: on a weak network the
    // resume refresh may have just failed transiently. Clearing here would kick the user out mid-session
    // on bad connectivity. This mirrors the route guard (okta-auth-guard) and AppComponent's resume/cold
    // -start flows, which keep the user in (offline mode) while a valid refresh token exists — a later
    // refresh (interceptor/guard/next resume) restores the access token.
    const okta = this.oktaAuthService.getOktaAuth();
    const refreshToken = await okta.tokenManager.get('refreshToken');
    if (refreshToken && !okta.tokenManager.hasExpired(refreshToken)) {
      this.userService.emitAuthStatusValue(true);
      return;
    }

    // No valid refresh token either — the user is genuinely logged out. Clear and send to login.
    if (!this.oktaAuthService.getIsLoggingIn()) {
      this.oktaAuthService.setIsLoggingIn(true);
      try {
        this.oktaAuthService.getOktaAuth().tokenManager.clear();
        const hasCritical = this.updateCheckService.hasShownCriticalUpdate;
        const hasNonCritical = this.updateCheckService.hasShownNonCriticalUpdate;
        if (!hasCritical && !hasNonCritical) {
          this.zone.run(() => this.router.navigateByUrl('/'));
        }
      } finally {
        this.oktaAuthService.setIsLoggingIn(false);
      }
    }
  }
}
